import React, { useMemo } from 'react';
import { Button, Callout, Icon, Intent } from '@blueprintjs/core';
import { observer } from 'mobx-react-lite';

import styled from 'styled-components';

import convertBackgroundToAbsoluteUrl from 'editor-utils/polotno/backgrounds/convertBackgroundToAbsoluteUrl';
import convertTextSpecToCss from 'editor-utils/polotno/texts/convertTextSpecToCss';
import getPlaceholderSpec from 'editor-utils/polotno/placeholder/getPlaceholderSpec';
import setPageStyle from 'editor-utils/polotno/pages/setPageStyle';
import setTextStyle from 'editor-utils/polotno/texts/setTextStyle';
import findLabelStyle from '../styles/findLabelStyle';
import getStyleCategoryLabel from '../styles/getStyleCategoryLabel';
import parseStyleName from '../styles/parseStyleName';
import stringifyStyleName from '../styles/stringifyStyleName';
import useEditorItemContext from '../../context/editorItemContext';
import SectionPanel from '../components/sectionPanel';
import SectionTab from '../components/sectionTab';

const StyledButton = styled(Button)`
  position: relative;
  min-height: ${24 * 1.5 + 2 * 15}px;
  padding: 0;
  font-size: 24px;
  line-height: 1.5;
`;

const Image = styled.img`
  width: 100%;
  border-radius: 2px;
`;

const Title = styled.span`
  overflow: hidden;
  position: absolute;
  left: 15px;
  right: 15px;
  bottom: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const changeStyleCategory = (categoryName, categoryStyles, styleName) => {
  // Preserve the style name in category
  const { name } = parseStyleName(styleName);
  // Replace the category
  return {
    style: categoryStyles[name],
    styleName: stringifyStyleName({ category: categoryName, name }),
  };
};

const changePageStyle = (categoryName, categoryStyles, page, photos, template) => {
  // Get the current page style
  const currentStyleName = page.custom.styleName;

  // Find the page style for selected style category.
  const pageStyle = changeStyleCategory(categoryName, categoryStyles, currentStyleName);
  // If there is no page style in category, nothing is changed.
  if (pageStyle == null) {
    return;
  }
  // Change the active page style to selected category page style.
  const { style, styleName } = pageStyle;
  setPageStyle(page, style, styleName, photos, template);
};

const changeTextStyle = (categoryName, categoryStyles, element) => {
  // Get the current text style
  const currentStyleName = element.custom.styleName;
  // Texts without styles aren't changed.
  if (currentStyleName == null) {
    return;
  }
  // Get the new style and set it to the text.
  const { style, styleName } = changeStyleCategory(categoryName, categoryStyles, currentStyleName);
  setTextStyle(element, style, styleName);
};

const getBackgroundImage = (categoryName, categoryStyles, page, template) => {
  // Get the current page style
  const currentStyleName = page.custom.styleName;

  // Find the page style for selected style category.
  const pageStyle = changeStyleCategory(categoryName, categoryStyles, currentStyleName);
  // If there is no page style in category, no background is displayed.
  if (pageStyle == null) {
    return undefined;
  }
  // Return the selected category page background image.
  // TODO Support style background color, only image is displayed now.
  const { backgroundImage } = pageStyle.style;
  if (backgroundImage == null) {
    return undefined;
  }
  return convertBackgroundToAbsoluteUrl(backgroundImage, template);
};

const getLabelCss = (categoryName, categoryStyles) => {
  const style = findLabelStyle(categoryName, categoryStyles);
  if (style == null) {
    return undefined;
  }
  const spec = getPlaceholderSpec(style);
  return {
    ...convertTextSpecToCss(spec),
    color: undefined,
    fontSize: undefined,
  };
};

const StylesPanel = observer(({ store }) => {
  const { activePage } = store;
  const styleName = activePage?.custom.styleName;
  const { item, template } = useEditorItemContext();

  const buttons = useMemo(() => {
    // Create button for each style available and attach its needed data to it.
    return Object.entries(template.template.styles ?? {})
      .map(
        ([categoryName, categoryStyles]) => ({
          backgroundImage: getBackgroundImage(categoryName, categoryStyles, activePage, template),
          categoryName,
          categoryStyles,
          label: getStyleCategoryLabel(categoryName),
          labelCss: getLabelCss(categoryName, categoryStyles),
        }),
        [],
      )
      .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  }, []);

  const selectedButton = useMemo(() => {
    // Get the category of current page style, or don't select anything.
    if (styleName == null) {
      return undefined;
    }
    const { category } = parseStyleName(styleName);
    // Find the button with that cetegory and select it.
    return buttons.find(button => button.categoryName === category);
  }, [buttons, styleName]);

  const handleButtonClick = button => {
    if (activePage == null) {
      return;
    }
    const { categoryName, categoryStyles } = button;
    changePageStyle(categoryName, categoryStyles, activePage, item.photos, template);
    activePage.children.forEach(element => {
      switch (element.custom.type) {
        case 'text':
          changeTextStyle(categoryName, categoryStyles, element);
          break;
        default:
      }
    });
  };

  return (
    <SectionPanel scrollable>
      <Callout intent={Intent.PRIMARY}>Choose the style which you like.</Callout>
      {buttons.map(button => {
        const { backgroundImage } = button;
        return (
          <StyledButton
            className="mt-1 w-100"
            intent={button.categoryName === selectedButton?.categoryName ? 'primary' : ''}
            key={button.categoryName}
            onClick={() => handleButtonClick(button)}
            outlined
            style={button.labelCss}
          >
            {backgroundImage != null && <Image src={backgroundImage} crossOrigin="anonymous" />}
            <Title>{button.label}</Title>
          </StyledButton>
        );
      })}
    </SectionPanel>
  );
});

export default {
  name: 'styles',
  Tab: props => (
    <SectionTab name="Styles" {...props}>
      <Icon icon="style" />
    </SectionTab>
  ),
  Panel: StylesPanel,
};
